import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, FileText, FolderOpen, FolderTree, RefreshCw, X } from "lucide-react";
import { VaultFile } from "../types";
import { baseName, buildTree, TreeNode } from "../utils/vault";

interface VaultPanelProps {
  /** Absolute path of the open vault, or null when none is open. */
  root: string | null;
  files: VaultFile[];
  /** Path of the note showing in the editor, so the tree can highlight it. */
  activePath?: string;
  scanning: boolean;
  onPick: () => void;
  onOpenFile: (path: string) => void;
  onRefresh: () => void;
  onClose: () => void;
}

interface BranchProps {
  nodes: TreeNode[];
  depth: number;
  activePath?: string;
  collapsed: Set<string>;
  onToggle: (key: string) => void;
  onOpenFile: (path: string) => void;
  /** Parent folder path, so sibling folders with the same name stay distinct. */
  prefix: string;
}

const Branch = ({ nodes, depth, activePath, collapsed, onToggle, onOpenFile, prefix }: BranchProps) => (
  <>
    {nodes.map((node) => {
      const key = `${prefix}/${node.name}`;
      // Indent with padding rather than nesting <ul>s: keeps the row's hover and
      // active background spanning the full panel width.
      const indent = { paddingLeft: `${8 + depth * 12}px` };

      if (node.path) {
        return (
          <button
            key={key}
            type="button"
            className={node.path === activePath ? "vault-item file active" : "vault-item file"}
            style={indent}
            title={node.path}
            onClick={() => onOpenFile(node.path as string)}
          >
            <FileText size={14} aria-hidden />
            <span className="vault-item-name">{node.name}</span>
          </button>
        );
      }

      const isCollapsed = collapsed.has(key);
      return (
        <div key={key} className="vault-branch">
          <button
            type="button"
            className="vault-item folder"
            style={indent}
            aria-expanded={!isCollapsed}
            onClick={() => onToggle(key)}
          >
            {isCollapsed ? <ChevronRight size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
            <span className="vault-item-name">{node.name}</span>
          </button>
          {!isCollapsed && (
            <Branch
              nodes={node.children}
              depth={depth + 1}
              activePath={activePath}
              collapsed={collapsed}
              onToggle={onToggle}
              onOpenFile={onOpenFile}
              prefix={key}
            />
          )}
        </div>
      );
    })}
  </>
);

export const VaultPanel = ({
  root,
  files,
  activePath,
  scanning,
  onPick,
  onOpenFile,
  onRefresh,
  onClose,
}: VaultPanelProps) => {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const tree = useMemo(() => buildTree(files), [files]);

  const toggle = (key: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <aside className="panel vault-panel" aria-label="Vault">
      <div className="panel-title">
        <FolderTree size={18} />
        <span>Vault</span>
        {root && <span className="panel-count">{files.length}</span>}
        {root && (
          <>
            <button
              type="button"
              className="panel-action"
              title="Rescan folder"
              aria-label="Rescan folder"
              onClick={onRefresh}
            >
              <RefreshCw size={14} className={scanning ? "spinning" : undefined} />
            </button>
            <button type="button" className="panel-action" title="Close vault" aria-label="Close vault" onClick={onClose}>
              <X size={15} />
            </button>
          </>
        )}
      </div>

      {!root ? (
        <div className="vault-empty">
          <p className="muted">
            Open a folder to make every <code>.md</code> file in it searchable and linkable — including files you
            haven&apos;t opened as tabs.
          </p>
          <button type="button" className="vault-pick" onClick={onPick}>
            <FolderOpen size={15} aria-hidden />
            Open Folder…
          </button>
        </div>
      ) : (
        <>
          <button type="button" className="vault-root" title={root} onClick={onPick}>
            <FolderOpen size={13} aria-hidden />
            <span className="vault-item-name">{baseName(root)}</span>
          </button>
          {files.length === 0 ? (
            <p className="muted">{scanning ? "Scanning…" : "No Markdown files in this folder."}</p>
          ) : (
            <nav className="vault-tree">
              <Branch
                nodes={tree}
                depth={0}
                activePath={activePath}
                collapsed={collapsed}
                onToggle={toggle}
                onOpenFile={onOpenFile}
                prefix=""
              />
            </nav>
          )}
        </>
      )}
    </aside>
  );
};
